// The live activity feed: the backend's change feed (paths, never values) and sign-ups through the
// console proxy, polled every 5 s while the tab is visible, with bursts on one collection collapsed
// into one row. Mail and push problems join from the control plane.
import { useMemo, useRef, useState } from "react";
import { myMail, tenant, unavailable } from "../../api.ts";
import { ago, fmt } from "../../fmt.ts";
import { useNow, usePoll } from "../../data/hooks.ts";
import { projectHref } from "../../router.ts";
import { Empty, Failed } from "../../ui.tsx";
import { ProductFrame, useProject } from "./ProjectPage.tsx";

type Item =
  | { kind: "write"; seq: number; at: number; path: string; op: "create" | "update" | "delete"; origin: string }
  | { kind: "signup"; at: number; provider: string }
  | { kind: "mail"; at: number; status: string; subject: string };
interface Row { key: string; at: number; icon: string; bad?: boolean; text: string; path?: string; kind: string }

const collectionOf = (path: string) => path.split("/").filter((_, i) => i % 2 === 0).join("/");

/** Collapses consecutive writes to one collection within 10 s into "posts · 37 writes in 10 s". */
function rows(items: Item[]): Row[] {
  const out: Row[] = [];
  const sorted = [...items].sort((a, b) => b.at - a.at);
  for (const it of sorted) {
    if (it.kind === "write") {
      const col = collectionOf(it.path);
      const prev = out[out.length - 1];
      if (prev && prev.kind === `w:${col}` && prev.at - it.at < 10_000) {
        const n = Number(prev.key.split("|")[1]) + 1;
        out[out.length - 1] = { ...prev, key: `${prev.key.split("|")[0]}|${n}`, text: `${n} writes in ${Math.max(1, Math.round((prev.at - it.at) / 1000))} s`, path: col };
        continue;
      }
      out.push({ key: `w${it.seq}|1`, at: it.at, kind: `w:${col}`, icon: it.op === "delete" ? "−" : it.op === "create" ? "+" : "~", text: `${it.op === "create" ? "created" : it.op === "delete" ? "deleted" : "updated"}${it.origin && it.origin !== "client" ? ` · ${it.origin}` : ""}`, path: it.path });
    } else if (it.kind === "signup") {
      out.push({ key: `s${it.at}${it.provider}`, at: it.at, kind: "signup", icon: "☺", text: `New user · ${it.provider}` });
    } else {
      const bad = ["bounced", "complained", "failed"].includes(it.status);
      out.push({ key: `m${it.at}${it.subject}`, at: it.at, kind: "mail", icon: "✉", bad, text: `Email ${it.status}: ${it.subject}` });
    }
  }
  return out;
}

export function useFeed(id: string, live: boolean) {
  const [items, setItems] = useState<Item[]>([]);
  const [state, setState] = useState<"loading" | "ok" | "unavailable" | "error">("loading");
  const [err, setErr] = useState<unknown>(null);
  const last = useRef(0);
  const lastAt = useRef(0);
  const [updated, setUpdated] = useState(0);
  usePoll(async () => {
    try {
      const [f, mail] = await Promise.all([
        // `after` pages the change feed; `since` keeps sign-ups from repeating (docs/console-api.md, as built).
        tenant(id)<{ items: Item[]; last: number; lastAt?: number }>(`feed?after=${last.current}&limit=200${lastAt.current ? `&since=${lastAt.current}` : ""}`),
        last.current ? Promise.resolve(null) : myMail(id)("messages?limit=15").then((r) => r.data as { created_at: number; status: string; subject: string }[]).catch(() => null),
      ]);
      last.current = f.last || last.current;
      lastAt.current = f.lastAt || lastAt.current;
      setItems((cur) => {
        const next = [...f.items, ...(mail ?? []).map((m) => ({ kind: "mail" as const, at: m.created_at, status: m.status, subject: m.subject })), ...cur];
        return next.slice(0, 500);
      });
      setState("ok");
      setUpdated(Date.now());
    } catch (e) {
      if (unavailable(e)) setState("unavailable");
      else {
        setErr(e);
        setState("error");
      }
    }
  }, 5000, live);
  return { rows: useMemo(() => rows(items), [items]), state, err, updated };
}

export function FeedList({ list, limit, id }: { list: Row[]; limit?: number; id: string }) {
  useNow(5000);
  const shown = limit ? list.slice(0, limit) : list;
  return (
    <ul className="feed" aria-live="polite">
      {shown.map((r) => (
        <li key={r.key}>
          <span className={`k${r.bad ? " bad" : ""}`} aria-hidden="true">{r.icon}</span>
          <span>{r.path && <a className="path" href={`${projectHref(id, "database", "data")}/${r.path.split("/").map(encodeURIComponent).join("/")}`}>{r.path}</a>} <span className="muted small">{r.text}</span></span>
          <span className="t">{ago(r.at)}</span>
        </li>
      ))}
    </ul>
  );
}

const KINDS = [{ id: "all", label: "All" }, { id: "w", label: "Database" }, { id: "signup", label: "Sign-ups" }, { id: "mail", label: "Mail" }];

export function Activity() {
  const { id, live } = useProject();
  const feed = useFeed(id, live);
  const [kind, setKind] = useState("all");
  const list = feed.rows.filter((r) => kind === "all" || r.kind === kind || r.kind.startsWith(`${kind}:`));
  return (
    <ProductFrame product="activity" actions={<span className="row small muted"><span className={`live-dot${feed.state === "ok" ? "" : " off"}`} />{feed.state === "ok" ? `Live · updated ${ago(feed.updated)}` : "Paused"}</span>}>
      <div className="row" style={{ marginBottom: 16 }}>
        <div className="seg">{KINDS.map((k) => <button key={k.id} className={k.id === kind ? "on" : ""} onClick={() => setKind(k.id)}>{k.label}</button>)}</div>
        <span className="small muted">{fmt.int(list.length)} events since you opened this page</span>
      </div>
      {!live ? <Empty title="Your backend isn't live yet.">Activity shows up here once it is.</Empty>
        : feed.state === "unavailable" ? <Empty title="The live feed starts with your backend's next update.">Nothing to do on your side.</Empty>
        : feed.state === "error" ? <Failed error={feed.err} />
        : !list.length ? <Empty title="Quiet right now.">Writes, sign-ups and emails appear here as they happen. Try writing a document from your app.</Empty>
        : <FeedList list={list} id={id} />}
    </ProductFrame>
  );
}
